import { useUserToken, useUserActions } from "@/store/userStore";
import { useCallback, useEffect, useState } from "react";
import { useRouter } from "../hooks";
import { buildApiUrl } from "@/config/api";
import { LineLoading } from "@/components/loading";

type Props = {
	children: React.ReactNode;
};
export default function LoginAuthGuard({ children }: Props) {
	const router = useRouter();
	const { accessToken } = useUserToken();
	const { clearUserInfoAndToken } = useUserActions();
	const [isValidating, setIsValidating] = useState(true);

	const validateToken = useCallback(async () => {
		if (!accessToken) {
			setIsValidating(false);
			router.replace("/auth/login");
			return;
		}

		try {
			// Use authStatus endpoint to validate the token
			const response = await fetch(buildApiUrl("/authStatus"), {
				method: "GET",
				headers: {
					Accept: "application/json",
					Authorization: `Bearer ${accessToken}`,
				},
			});

			if (!response.ok) {
				// Token is invalid, clear it and redirect to login
				if (response.status === 401 || response.status === 403) {
					clearUserInfoAndToken();
					sessionStorage.removeItem("token");
					sessionStorage.removeItem("permissions");
					sessionStorage.removeItem("user");
					router.replace("/auth/login");
				}
			}
		} catch (error) {
			// Network error or invalid token, clear and redirect
			console.error("Token validation error:", error);
			clearUserInfoAndToken();
			sessionStorage.removeItem("token");
			sessionStorage.removeItem("permissions");
			sessionStorage.removeItem("user");
			router.replace("/auth/login");
		} finally {
			setIsValidating(false);
		}
	}, [accessToken, router, clearUserInfoAndToken]);

	useEffect(() => {
		validateToken();
	}, [validateToken]);

	if (isValidating) {
		return <LineLoading />;
	}

	if (!accessToken) {
		return null;
	}

	return <>{children}</>;
}
